import {MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';

import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {Axis} from './common';
import {TileTitle} from './controls';
import {GridCell} from './layout/types';
import PanelTileList from './PanelTileList';
import {getTilesWithMaskOptions, TileWithMaskOptions} from './tiles';

function renderGridRow(n: React.ReactNode): JSX.Element {
  return <div className="panel-media__grid-row">{n}</div>;
}

// PanelTileGroup component for handling a single tile group and its grid row logic
const PanelTileGroup = React.memo<{
  tileOrGroup: any;
  tileIndex: number;
  cardWidth: number;
  cardHeight: number;
  config: any;
  allMasks: any;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  labels: Axis[];
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  columnCount: number;
  groupCount: number;
}>(
  ({
    tileOrGroup,
    tileIndex,
    cardWidth,
    cardHeight,
    config,
    allMasks,
    mediaType,
    mediaSliderKey,
    labels,
    panelRef,
    disableRunLinks,
    customRunNames,
    columnCount,
    groupCount,
  }) => {
    const emptyTile = (
      <div
        key={tileIndex}
        className="ui card"
        style={{
          width: cardWidth,
          height: cardHeight,
        }}
      />
    );

    if (tileOrGroup == null) {
      return emptyTile;
    }

    const tiles =
      'groupType' in tileOrGroup ? tileOrGroup.items : [tileOrGroup];
    const tilesToRender = getTilesWithMaskOptions({
      config,
      allMasks,
      tiles,
    });
    const firstTileOfGroup: TileWithMaskOptions | null =
      tilesToRender[0] ?? null;
    // Group has no renderable Tiles
    if (firstTileOfGroup == null || firstTileOfGroup.isEmpty) {
      return emptyTile;
    }

    const step = firstTileOfGroup.mediaStep;

    const showGridRow =
      config.mode === 'grid' &&
      columnCount &&
      (tileIndex * groupCount) % columnCount === 0;

    const gridYAxis = config.gridSettings?.yAxis;

    // Create a GridCell-like object for the TileTitle component
    const titleCell: GridCell = {
      run: firstTileOfGroup.run,
      step: firstTileOfGroup.mediaStep,
      mediaIndex: firstTileOfGroup.mediaIndex,
      mediaKey: firstTileOfGroup.mediaKey,
      tileSpec: {
        mediaType: config.mediaType ?? 'image',
        run: firstTileOfGroup.run,
        mediaStep: firstTileOfGroup.mediaStep,
        mediaIndex: firstTileOfGroup.mediaIndex,
        mediaKey: firstTileOfGroup.mediaKey,
        mediaSliderKey: config.mediaSliderKey ?? '_step',
        runSignature: firstTileOfGroup.runSignature,
      },
      labels: {
        run: firstTileOfGroup.run.displayName || firstTileOfGroup.run.name,
        step: firstTileOfGroup.mediaStep,
        index: firstTileOfGroup.mediaIndex,
      },
      maskOptions: firstTileOfGroup.maskOptions,
    };

    const gridRow =
      gridYAxis === 'run'
        ? renderGridRow(
            <TileTitle
              dimension="run"
              cell={titleCell}
              disableRunLinks={disableRunLinks}
            />
          )
        : gridYAxis === 'step'
          ? renderGridRow(
              <TileTitle
                dimension="step"
                cell={titleCell}
                disableRunLinks={disableRunLinks}
              />
            )
          : gridYAxis === 'index'
            ? renderGridRow(
                <TileTitle
                  dimension="index"
                  cell={titleCell}
                  disableRunLinks={disableRunLinks}
                />
              )
            : null;

    return (
      <React.Fragment key={tileIndex}>
        {showGridRow && gridRow}
        <PanelTileList
          tilesToRender={tilesToRender}
          tileIndex={tileIndex}
          cardWidth={cardWidth}
          cardHeight={cardHeight}
          config={config}
          mediaType={mediaType}
          mediaSliderKey={mediaSliderKey}
          labels={labels}
          panelRef={panelRef}
          disableRunLinks={disableRunLinks}
          customRunNames={customRunNames}
        />
      </React.Fragment>
    );
  }
);

export default PanelTileGroup;
